// Pure helpers behind the property card photo header: gallery URLs, token
// availability, compact counts and the stat-chip copy. Kept free of Vue so
// they can be unit-tested directly (see propertyCardParser.spec.ts).
import { PROPERTY_STATUS, type PropertyStatus } from '../../constants/propertyStatus'
import type { OfferingCardMediaWire } from '../contracts/propertyContracts'
import type { PropertyStatChip } from '../../types/propertyTypes'

const IMAGE_MEDIA_TYPE = 'image'
const FULL_PERCENT = 100
const THOUSAND = 1_000
const MILLION = 1_000_000
// Below this many units (e.g. 9.5K) the short count keeps one decimal.
const SHORT_COUNT_DECIMAL_LIMIT = 10

const ABSOLUTE_URL_RE = /^(?:[a-z][a-z\d+.-]*:|\/\/)/i

/** Absolute URLs (https:, data:, blob:, //cdn) pass through; relative paths
 *  resolve against `baseUrl`. Returns undefined for empty / unresolvable input. */
export const resolveMediaUrl = (
  path: string | null | undefined,
  baseUrl: string | undefined,
): string | undefined => {
  const trimmed = path?.trim()
  if (!trimmed) return undefined
  if (ABSOLUTE_URL_RE.test(trimmed) || !baseUrl) return trimmed
  try {
    return new URL(trimmed, baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`).toString()
  } catch {
    return undefined
  }
}

/**
 * Card photos: images with a filePath, cover first, then displayOrder
 * ascending; resolved, de-duplicated (the cover also arrives as the flat
 * `filePath`), falling back to `filePath` when the gallery is empty.
 */
export const buildCardImageUrls = (
  media: OfferingCardMediaWire[] | null | undefined,
  filePath: string | null | undefined,
  baseUrl: string | undefined,
): string[] => {
  const ordered = (media ?? [])
    .filter((item) => item.mediaType === IMAGE_MEDIA_TYPE && Boolean(item.filePath?.trim()))
    .sort((left, right) => {
      const coverRank = Number(Boolean(right.isCover)) - Number(Boolean(left.isCover))
      if (coverRank !== 0) return coverRank
      return (left.displayOrder ?? 0) - (right.displayOrder ?? 0)
    })
    .map((item) => resolveMediaUrl(item.filePath, baseUrl))
    .filter((url): url is string => Boolean(url))

  const unique = [...new Set(ordered)]
  if (unique.length > 0) return unique

  const fallback = resolveMediaUrl(filePath, baseUrl)
  return fallback ? [fallback] : []
}

interface TokenInputs {
  totalTokens: number | null | undefined
  fundedPercentage: number | null | undefined
  tokensAvailable: number | null | undefined
}

/** tokensAvailable ?? round(totalTokens × (100 − clamp(funded%, 0, 100)) / 100). */
export const computeTokensLeft = ({
  totalTokens,
  fundedPercentage,
  tokensAvailable,
}: TokenInputs): number | undefined => {
  if (tokensAvailable != null && Number.isFinite(tokensAvailable)) return tokensAvailable
  if (totalTokens == null || !Number.isFinite(totalTokens)) return undefined
  const funded = Math.min(FULL_PERCENT, Math.max(0, Number(fundedPercentage ?? 0)))
  return Math.round((totalTokens * (FULL_PERCENT - funded)) / FULL_PERCENT)
}

const formatScaled = (value: number, unit: string): string => {
  const digits = value < SHORT_COUNT_DECIMAL_LIMIT ? 1 : 0
  return `${value.toFixed(digits)}${unit}`
}

/** 540000 → "540K", 1_200_000 → "1.2M", 9_500 → "9.5K", 30 → "30". */
export const formatShortCount = (count: number): string => {
  if (count >= MILLION) return formatScaled(count / MILLION, 'M')
  if (count >= THOUSAND) return formatScaled(count / THOUSAND, 'K')
  return String(Math.round(count))
}

export const formatInvestors = (count: number): string =>
  `${count} ${count === 1 ? 'Investor' : 'Investors'}`

interface StatChipInputs {
  totalTokens: number | null | undefined
  tokensLeft: number | undefined
  totalInvestors: number | null | undefined
}

const TOKENS_LABEL_BUILDERS: Record<PropertyStatus, (left: number, total: number) => string> = {
  [PROPERTY_STATUS.LIVE]: (left, total) =>
    `${formatShortCount(left)} out of ${formatShortCount(total)} Tokens Left`,
  [PROPERTY_STATUS.FULLY_FUNDED]: () => 'All Tokens sold',
  [PROPERTY_STATUS.LAUNCHING_SOON]: (left) => `${formatShortCount(left)} Tokens available`,
}

/**
 * Stat chip copy over the card photo. Hidden (undefined) when totalTokens is
 * missing or 0. Launching Soon never gets the investors face; the others get
 * it only when the backend sends totalInvestors.
 */
export const buildStatChip = (
  status: PropertyStatus,
  { totalTokens, tokensLeft, totalInvestors }: StatChipInputs,
): PropertyStatChip | undefined => {
  if (!totalTokens) return undefined
  const tokensLabel = TOKENS_LABEL_BUILDERS[status](tokensLeft ?? totalTokens, totalTokens)
  const hasInvestors =
    status !== PROPERTY_STATUS.LAUNCHING_SOON &&
    totalInvestors != null &&
    Number.isFinite(totalInvestors)
  return hasInvestors
    ? { tokensLabel, investorsLabel: formatInvestors(totalInvestors) }
    : { tokensLabel }
}
